/**
 * AddressSearchField — 인라인 도로명주소 검색 위젯
 * 모바일 AddressSearchField 동일 패턴: 디바운스 600ms, 최소 2자, 인라인 펼침/접음
 * 주소 선택 후 POST /indipia/address/coord 자동 호출 → onSelect({ roadAddress, gpsLat, gpsLng })
 */
import { useState, useRef, useCallback, useId } from 'react';
import { Button } from '@indipia/ui-core';
import { searchAddress, fetchCoord, AddressResult } from '../api/address';

export interface AddressSelectResult {
  roadAddress: string;
  gpsLat:      number | null;
  gpsLng:      number | null;
}

interface Props {
  value:       string;
  onSelect:    (result: AddressSelectResult) => void;
  label?:      string;
  required?:   boolean;
  placeholder?: string;
  error?:      string | null;
}

export function AddressSearchField({
  value,
  onSelect,
  label = '주소',
  required = false,
  placeholder = '도로명, 건물명으로 검색',
  error = null,
}: Props) {
  const [isOpen,     setIsOpen]     = useState(false);
  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState<AddressResult[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [coordFetching, setCoordFetching] = useState(false);
  const [searched,   setSearched]   = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerId   = useId();

  const open = () => {
    setIsOpen(true);
    setQuery('');
    setResults([]);
    setSearched(false);
    setSearchError(null);
  };

  const close = () => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    setSearched(false);
    setSearchError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleQueryChange = useCallback((q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); setSearched(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setSearchError(null);
      try {
        const data = await searchAddress(q);
        setResults(data);
        setSearched(true);
      } catch (err) {
        // 장애는 '결과 없음'과 구분해 서버 메시지로 표시한다
        const m = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setResults([]);
        setSearched(true);
        setSearchError(m ?? '주소 검색에 실패했어요. 잠시 후 다시 시도해주세요');
      } finally {
        setLoading(false);
      }
    }, 600);
  }, []);

  const select = async (r: AddressResult) => {
    close();
    setCoordFetching(true);
    try {
      const coord = await fetchCoord(r);
      onSelect({ roadAddress: r.roadAddress, gpsLat: coord.latitude, gpsLng: coord.longitude });
    } catch {
      // 좌표 조회 실패 시 주소만 전달
      onSelect({ roadAddress: r.roadAddress, gpsLat: null, gpsLng: null });
    } finally {
      setCoordFetching(false);
    }
  };

  return (
    /* 라벨·필수표시·오류문구는 `.field` 표준 슬롯을 쓴다 — `.field.error` 가 `.input` 트리거의
       테두리까지 함께 물들인다(D-09 §15.2). ui-core `Input` 을 쓰지 않는 이유는 아래 트리거 주석 참조. */
    <div className={`field field-fill ${error ? 'error' : ''}`}>
      {label && (
        <label htmlFor={triggerId}>
          {label}
          {required && <span className="req" aria-hidden="true">*</span>}
        </label>
      )}

      {!isOpen ? (
        value ? (
          /* 선택 완료 상태 */
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
            padding: 'var(--spacing-04) var(--spacing-05)', background: 'var(--shell-bg)',
            border: '1px solid var(--color-success)', borderRadius: 'var(--radius-lg)',
            opacity: coordFetching ? 0.6 : 1,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            <span className="text-body-sm text-text" style={{ flex: 1, wordBreak: 'break-all' }}>
              {coordFetching ? '좌표 조회 중…' : value}
            </span>
            {!coordFetching && (
              <>
                <Button variant="ghost" size="sm" className="shrink-0" onClick={open}>수정</Button>
                {/* 필드 안에 얹히는 어펜드 아이콘 — `.btn-icon` 은 48 정사각이라 여기 들어가지 못한다(D-09 §15.1) */}
                <button
                  type="button"
                  onClick={() => onSelect({ roadAddress: '', gpsLat: null, gpsLng: null })}
                  aria-label="주소 지우기"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--spacing-02)', color: 'var(--shell-hint)', display: 'flex', flexShrink: 0 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </>
            )}
          </div>
        ) : (
          /* 미선택 상태 — 검색 패널을 여는 트리거.
             이건 버튼 모양의 액션이 아니라 **입력 필드 그 자체**라 `.input` 규격을 입힌다
             (높이·테두리·radius·포커스 링을 옆 필드들과 맞춘다, D-09 §15.2).
             ui-core `Input` 은 readonly 트리거로 쓰면 아이콘 슬롯이 없어 `.input` 클래스를 직접 얹는다. */
          <button
            id={triggerId}
            type="button"
            onClick={open}
            className="input input-lg text-body-sm"
            style={{
              textAlign: 'left', cursor: 'pointer', color: 'var(--shell-hint)',
              display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            {placeholder}
          </button>
        )
      ) : (
        /* 열린 상태 — 검색창 + 결과 목록. 패널이 테두리·포커스 표현을 소유하므로
           안쪽 입력은 무테(composite 내부 요소)로 둔다 — 이중 테두리 방지. */
        <div style={{ border: '1px solid var(--gt-primary)', boxShadow: '0 0 0 3px var(--gt-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--shell-surface)' }}>
          {/* 입력 행 */}
          <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--spacing-03) var(--spacing-04)', gap: 'var(--gap-sm)', borderBottom: '1px solid var(--shell-border)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              autoFocus
              type="text"
              aria-label={label}
              placeholder="도로명, 건물명 입력"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              className="text-body-sm"
              style={{
                flex: 1, border: 'none', outline: 'none', minWidth: 0,
                color: 'var(--shell-on-surface)', background: 'transparent', fontFamily: 'inherit',
              }}
            />
            {/* 필드 안에 얹히는 어펜드 아이콘 — `.btn-icon`(48 정사각) 대상 아님 */}
            <button
              type="button"
              onClick={close}
              aria-label="주소 검색 닫기"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--spacing-01)', color: 'var(--shell-on-surface-2)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* 결과 목록 — 리스트 아이템이라 `.btn` 규격 대상이 아니다(D-09 §15.1은 액션 버튼 규격) */}
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {loading ? (
              <div className="text-caption text-text-secondary" style={{ padding: 'var(--spacing-05)', textAlign: 'center' }}>검색 중…</div>
            ) : query.length < 2 ? (
              <div className="text-caption text-text-secondary" style={{ padding: 'var(--spacing-05)' }}>2자 이상 입력하면 검색합니다</div>
            ) : searchError ? (
              <div className="text-caption text-text-secondary" style={{ padding: 'var(--spacing-05)' }}>{searchError}</div>
            ) : searched && results.length === 0 ? (
              <div className="text-caption text-text-secondary" style={{ padding: 'var(--spacing-05)' }}>검색 결과가 없어요</div>
            ) : (
              results.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => void select(r)}
                  className="hover:bg-surface"
                  style={{
                    width: '100%', padding: 'var(--spacing-04) var(--spacing-05)', textAlign: 'left',
                    background: 'none', border: 'none', cursor: 'pointer',
                    borderTop: i > 0 ? '1px solid var(--shell-border)' : 'none',
                    fontFamily: 'inherit', display: 'block',
                  }}
                >
                  <div className="text-body-sm font-medium text-text" style={{ marginBottom: r.jibunAddress ? 2 : 0 }}>
                    {r.roadAddress}
                  </div>
                  {r.jibunAddress && (
                    <div className="text-caption text-text-secondary">
                      {r.jibunAddress}{r.buildingName ? ` · ${r.buildingName}` : ''}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {error && <span className="help">{error}</span>}
    </div>
  );
}
